<template>
    <main>
        <AsyncProfileViewBlocker v-if="isUnloggedUser" :open="showProfileViewBlocker" />
        <div ref="scrollWrapper" class="profile-id-or-alias profile-wrapper joy-wrapper profile-wrapper--grey">
            <div class="profile-wrapper__profile">
                <ShortlistHeader v-if="!profile?.editMode && !comesFromAdvancedSearch" :account-id="profile?.accountId" />

                <div class="profile-wrapper__layout">
                    <div class="profile-wrapper__main">
                        <div class="profile-wrapper__header">
                            <BreadcrumbSection />
                            <LazyProfileHeaderSectionEdit v-if="profile?.editMode" />
                            <ProfileHeaderSectionReadOnly v-else />
                        </div>
                        <NavigationTabs />
                        <VJoyWrapper
                            v-if="sectionVisibility[SectionsVisibilityType.VISIBILITY_ALERT_BLOCK]"
                            class="profile-wrapper__profile-header-alerts"
                            item-stretch
                            no-margin
                            row-gap="4"
                        >
                            <LazyAlertsSection />
                            <LazyPhotoAlert v-if="shouldDisplayProfilePictureAlert" />
                        </VJoyWrapper>
                        <div class="profile-wrapper__content">
                            <div class="profile-wrapper__main-content">
                                <MainContentSection />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ClientOnly v-if="displayLoggedSection">
                <AsyncEditionComponentsWrapper v-if="profile?.editMode" />
                <AsyncExternalUserComponentsWrapper v-else-if="loggedIn" />
                <AsyncAdminComponentsWrapper v-if="isAdmin" :ref-to-scroll-target="scrollWrapper" />
            </ClientOnly>
        </div>
        <ClientOnly>
            <LazyAmplitudeSessionReplayStarter v-if="shouldStartAmplitudeSessionReplay" />
            <LazyPortfolioItemOverlayWrapper />
        </ClientOnly>
    </main>
</template>

<script lang="ts" setup>
    import {computed, defineAsyncComponent, onBeforeUnmount, onMounted, ref, useTemplateRef, watch} from 'vue';
    import {storeToRefs} from 'pinia';
    import {
        ClientOnly,
        LazyAlertsSection,
        LazyAmplitudeSessionReplayStarter,
        LazyPhotoAlert,
        LazyPortfolioItemOverlayWrapper,
        LazyProfileHeaderSectionEdit,
    } from '#components';
    import {
        callOnce,
        definePageMeta,
        useAsyncData,
        useAuth,
        useGuestHost,
        useHasEnoughPriviledge,
        useHead,
        useHTMLAttrsLang,
        useInfosRequest,
        useRoute,
        useRouter,
        useSeoMeta,
        useTranslation,
    } from '#imports';
    import {Photo} from '@malt/asset-utils';
    import {useABTest, useFeatureFlag} from '@malt/feature-flags';
    import {safelyNavigateTo, usePageCache} from '@malt/utils';
    import {pushVJoySnackbar, VJoyWrapper} from '@maltjoy/core-vue';
    import MainContentSection from '~/components/section/MainContentSection.vue';
    import ShortlistHeader from '~/components/ShortlistHeader.vue';
    import {useAddRecommendationQueryParam} from '~/composables/useAddRecommendationQueryParam';
    import {useComesFromAdvancedSearch} from '~/composables/useComesFromAdvancedSearch';
    import {useDomainChecker} from '~/composables/useDomainChecker';
    import {useProfileTracking} from '~/composables/useProfileTracking';
    import {useBreadcrumbMicroData, useProfileMicroData} from '~/composables/useSeoProfileHelper';
    import {ProfileViewedOrigin, ProfileViewMode} from '~/config/TrackingEvents';
    import {useAppraisalStore} from '~/features/appraisals/store/appraisalStore';
    import BreadcrumbSection from '~/features/breadcrumb/BreadcrumbSection.vue';
    import {usePortfolioItemsStore} from '~/features/portfolio/section/store/portfolio-items.store';
    import NavigationTabs from '~/features/profile-header/ui/NavigationTabs.vue';
    import ProfileHeaderSectionReadOnly from '~/features/profile-header/ui/profile-header-section/ProfileHeaderSectionReadOnly.vue';
    import {useProfileSuggestionsStore} from '~/features/profile-suggestions/store/profile-suggestions.store';
    import {useRecommendationsStore} from '~/features/recommendations/store/recommendations.store';
    import {useInstantConversationStore} from '~/store/instantConversation.store';
    import {useMicroConsultingStore} from '~/store/micro-consulting/microConsultingStore';
    import {useProfileStore} from '~/store/profileStore';
    import {useReputationStore} from '~/store/reputation/reputationStore';
    import {useSectionsVisibilityStore} from '~/store/sectionsVisibility';
    import {useSimilarProfileStore} from '~/store/similars/similarProfiles.store';
    import {useSkillSetStore} from '~/store/skill-set/skillSetStore';
    import {useAsyncTranslationStore} from '~/store/translation/asyncTranslationStore';
    import {useWebPreferencesStore} from '~/store/webPreferences.store';
    import {useWishlistStore} from '~/store/wishlist/wishlistStore';
    import {SectionsVisibilityType} from '~/types/domain/SectionsVisibility';
    import {useShortlistStore} from '@/store/shortlist/ShortlistStore';

    definePageMeta({
        middleware: ['profile-alias-and-other-redirect', 'draft-instant-conversation-save-and-redirect'],
        layout: 'malt-full-layout',
    });

    // Composables
    const {t} = useTranslation();
    const profileStoreRequest = useInfosRequest();
    const route = useRoute();
    const router = useRouter();
    useAddRecommendationQueryParam();

    // Stores
    const shortlistStore = useShortlistStore();
    const {getWishlists} = useWishlistStore();
    const profileStore = useProfileStore();
    const {listPortfolioItems} = usePortfolioItemsStore();
    const {fetchSkillSet} = useSkillSetStore();
    const {initTranslationIfNeeded} = useAsyncTranslationStore();
    const {getReputations} = useReputationStore();
    const isClientInstantConversationEnabled = useFeatureFlag('client-instant-conversation');
    const isPortfolioRevampV1Enabled = useFeatureFlag('portfolio-revamp-v1');
    const isWorkshopAiNantesPortfolioRevampEnabled = useFeatureFlag('workshop-ai-nantes-portfolio-revamp');
    const isEnableProfilePageCacheEnabled = useFeatureFlag('enable-profile-page-cache');
    const isLimitInstantConversationPerDayEnabled = useFeatureFlag('limit-number-of-instant-conversation-per-day');
    const isEnableMicroConsultingOnProfileSectionEnabled = useFeatureFlag('enable-micro-consulting-on-profile-section');
    const isEnableMicroConsultingBookingEnabled = useFeatureFlag('enable-micro-consulting-booking');
    const isMaltComSeoEnabled = useFeatureFlag('new-profile-malt-com-seo-logic');
    const isAmplitudeRecordingForProfileNonSelfViewEnabled = useFeatureFlag('amplitude-recording-for-profile-non-self-view');
    const isAmplitudeRecordingForProfileSelfViewEnabled = useFeatureFlag('amplitude-recording-for-profile-self-view');
    const isAmplitudeRecordingForProfileUnlogguedViewEnabled = useFeatureFlag('amplitude-recording-for-profile-unloggued-view');
    const {displayName, profile} = storeToRefs(profileStore);
    const {getAppraisals} = useAppraisalStore();
    const {
        getProfile,
        getBreadcrumb,
        getPortfolio,
        getVerificationChecklist,
        getSectionsVisibility,
        getRelations,
        reloadExperiences,
        reloadIndustryExpertises,
        reloadEducations,
        reloadCertifications,
    } = profileStore;
    const {getWebPreferences} = useWebPreferencesStore();
    const {loadInstantConversationUsage} = useInstantConversationStore();
    const {getVariationWithoutTracking} = useABTest();
    const {getRecommendations} = useRecommendationsStore();
    const {getSimilarProfiles} = useSimilarProfileStore();
    const {getClientEligibility, getFreelancerEligibility, getMicroConsultingInfo} = useMicroConsultingStore();
    const {getSuggestions} = useProfileSuggestionsStore();
    const {trackProfileViewed} = useProfileTracking();
    const authStore = useAuth();
    const {currentIdentityIsClient, currentIdentityIsFreelance, isCompanySuspended, user, loggedIn} = storeToRefs(authStore);
    const {refresh} = authStore;

    // Refs
    const hasAnErrorInQuery = ref(Object.prototype.hasOwnProperty.call(route.query, 'error'));
    const hasSuccessInQuery = ref(Object.prototype.hasOwnProperty.call(route.query, 'success'));
    const photoUrl = ref<string>('');
    const scrollWrapper = useTemplateRef('scrollWrapper');
    const showProfileViewBlocker = ref<boolean>(false);

    // Computed values
    const errorFromQuery = computed<string | undefined>(() => route.query.error as string);
    const {sectionVisibility} = storeToRefs(useSectionsVisibilityStore());
    const comesFromAdvancedSearch = useComesFromAdvancedSearch();
    const openedFromSimilarProfiles = computed<boolean>(() => !!route?.query?.reco);
    const isInOverviewMode = computed<boolean>(() => route?.fullPath?.includes('overview') || false);
    const openedFromRecommendationList = computed<boolean>(() => !!route?.query?.fromRecommendationList);

    const profileOrigin = computed<ProfileViewedOrigin>(() => {
        if (openedFromSimilarProfiles.value) {
            return ProfileViewedOrigin.SIMILAR_PROFILE;
        }
        if (openedFromRecommendationList.value) {
            return ProfileViewedOrigin.PROFILE_RECOMMENDATION_LIST;
        }
        if (isInOverviewMode.value) {
            return ProfileViewedOrigin.FREELANCER_AS_CLIENT;
        }
        if (
            route?.query?.origin == ProfileViewedOrigin.DASHBOARD_UPDATE_PROFILE ||
            route?.query?.origin == ProfileViewedOrigin.DASHBOARD_PROFILE_NAME ||
            route?.query?.origin == ProfileViewedOrigin.SITE_NAV_BAR ||
            route?.query?.origin == ProfileViewedOrigin.MICRO_CONSULTING_OFFER_PAGE
        ) {
            return route?.query?.origin as ProfileViewedOrigin;
        } else {
            return ProfileViewedOrigin.DIRECT_URL;
        }
    });

    const displayLoggedSection = computed((): boolean => !!loggedIn.value || !!profile.value?.editMode || isAdmin);
    const mode = computed((): ProfileViewMode => (profile.value?.editMode ? ProfileViewMode.EDITION : ProfileViewMode.READ_ONLY));
    const isInstantConversationEnabled = computed(
        (): boolean => isClientInstantConversationEnabled.value && getVariationWithoutTracking('instant-conversation-v2') === 'treatment',
    );
    const shouldDisplayProfilePictureAlert = computed(() => profile.value?.photo?.brokenOrFallbackImage);
    const isUnloggedUser = computed(() => !loggedIn.value);
    const isOwner = computed(() => user.value?.id === profile.value?.accountId);

    const isAdmin = await useHasEnoughPriviledge(['ROLE_ADMIN']);

    const {data: mandatoryData, error: errorFetchingMandatoryData} = await useAsyncData(
        'profile-id-or-alias_mandatory',
        () => getProfile(profileStoreRequest),
        {deep: true},
    );

    if (!mandatoryData.value || errorFetchingMandatoryData.value) {
        throw new Error(`Error while fetching mandatory data:\n ${errorFetchingMandatoryData.value}`, {cause: errorFetchingMandatoryData});
    }

    if (loggedIn.value && isCompanySuspended.value && profile.value?.accountId !== user.value?.id) {
        await safelyNavigateTo('/client-settings/restricted', {externalToThisApp: true});
    }

    if (!loggedIn.value && isEnableProfilePageCacheEnabled.value) {
        usePageCache();
    }

    // All these calls are based on profile ref
    await callOnce('profile-id-or-alias_secondary', async () => {
        await Promise.all([
            getAppraisals(),
            reloadExperiences(),
            reloadIndustryExpertises(),
            reloadEducations(),
            reloadCertifications(),
            getRecommendations(),
            getBreadcrumb(),
            getVerificationChecklist(),
            fetchSkillSet(),
            getReputations(),
            getSectionsVisibility(profileStoreRequest.details.route.queryParameters),
            shortlistStore.reload(),
            loggedIn.value && (isPortfolioRevampV1Enabled.value || (isWorkshopAiNantesPortfolioRevampEnabled.value && profile.value!.editMode))
                ? listPortfolioItems(profile.value!.profileId)
                : Promise.resolve(),
            loggedIn.value && !isPortfolioRevampV1Enabled.value && !(isWorkshopAiNantesPortfolioRevampEnabled.value && profile.value!.editMode)
                ? getPortfolio()
                : Promise.resolve(),
            loggedIn.value && !profile.value!.editMode ? getWishlists(profile.value!) : Promise.resolve(),
            loggedIn.value && !profile.value!.editMode ? getRelations() : Promise.resolve(),
            loggedIn.value && !profile.value!.editMode ? getSimilarProfiles() : Promise.resolve(),
            loggedIn.value ? getWebPreferences(user.value!.id) : {},
            loggedIn.value && isLimitInstantConversationPerDayEnabled.value && isInstantConversationEnabled.value
                ? loadInstantConversationUsage()
                : Promise.resolve(),
            loggedIn.value && (isEnableMicroConsultingOnProfileSectionEnabled.value || isEnableMicroConsultingBookingEnabled.value)
                ? getMicroConsultingInfo()
                : Promise.resolve(),
            loggedIn.value && currentIdentityIsClient.value && isEnableMicroConsultingBookingEnabled.value
                ? getClientEligibility()
                : Promise.resolve(),
            loggedIn.value && currentIdentityIsFreelance.value && isEnableMicroConsultingOnProfileSectionEnabled.value
                ? getFreelancerEligibility()
                : Promise.resolve(),
            loggedIn.value && profile.value!.editMode ? getSuggestions() : Promise.resolve(),
        ]);
        await initTranslationIfNeeded(true);
    });

    // Watchers
    watch(
        () => profile.value?.photo,
        (newPhotoUrl) => {
            photoUrl.value = newPhotoUrl
                ? new Photo(newPhotoUrl).getFaceThumbnailUrl({
                      width: 330,
                      height: 325,
                      forcedFormat: 'png',
                  })
                : '';
        },
        {immediate: true},
    );

    // Lifecycle hooks
    onMounted(async () => {
        if (hasAnErrorInQuery.value) {
            pushVJoySnackbar({
                props: {
                    message:
                        errorFromQuery.value === 'instant-conversation-limit-reached'
                            ? t('profile.instant.conversation.limit.reached')
                            : t('generic.error.unexpected'),
                    level: 'error',
                },
            });
            const queryWithoutError = {...route.query};
            delete queryWithoutError.error;
            router.push({query: queryWithoutError});
        }

        if (hasSuccessInQuery.value) {
            const query = {...route.query};
            pushVJoySnackbar({
                props: {
                    message: t('account.update.success'),
                },
            });
            delete query.success;
            await router.push({query});
        }

        if (profile.value?.editMode === true) {
            window.addEventListener('focus', handleWindowFocusInEditMode);
        }

        const searchId = route?.query?.searchid as string;
        const advancedSearchId = route?.query?.advancedSearchId as string;
        const sourcingHubCatalogId = route?.query?.catalogId as string;
        const microConsultingSearchId = route?.query?.microConsultingSearchId as string;

        trackProfileViewed(
            searchId,
            advancedSearchId,
            sourcingHubCatalogId,
            profile.value?.accountId,
            profileOrigin.value,
            mode.value,
            isOwner.value,
            microConsultingSearchId,
        );

        if (isUnloggedUser.value) {
            window.addEventListener('scroll', handleProfileViewBlocker);
        }
    });

    onBeforeUnmount(() => {
        window.removeEventListener('focus', handleWindowFocusInEditMode);
        window.removeEventListener('scroll', handleProfileViewBlocker);
    });

    // Handlers
    async function handleWindowFocusInEditMode() {
        await refresh();
        if (loggedIn.value !== true) {
            window.location.reload();
        }
    }

    function handleProfileViewBlocker() {
        if (window.scrollY > window.innerHeight / 2) {
            showProfileViewBlocker.value = true;
        }
    }

    // Register on both server and client. Unhead 3 keeps app.head (nuxt-base-config)
    // defaults active on the client; server-only useSeoMeta loses the description /
    // og / twitter overrides after hydration.
    const isIndexable = !openedFromSimilarProfiles.value && profile.value?.privacy.indexableOnSearchEngines;
    const openGraphImage = 'https://dam.malt.com/profile/profile-open-graph.jpg';

    useSeoMeta({
        fbAppId: '2305272732',
        description: t('profile.static.meta', {
            0: displayName.value,
            1: profile.value?.headline,
        }),
        profileUsername: displayName.value,
        robots: isIndexable ? 'noimageindex' : 'noindex',
        ogType: 'profile',
        ogTitle: t('profile.static.ogtitle', {0: displayName.value, 1: profile.value?.headline}),
        ogImage: openGraphImage,
        ogDescription: t('meta.property.profile.desc', {0: displayName.value}),
        ogUrl: profile.value?.profileUrl,
        twitterCard: 'summary',
        twitterImage: openGraphImage,
        twitterTitle: t('profile.static.ogtitle.twitter', {0: displayName.value, 1: profile.value?.headline}),
        twitterDescription: t('profile.static.meta', {
            0: displayName.value,
            1: profile.value?.headline,
        }),
    });

    const linkConfig = [];
    const {isMaltDotCom} = useDomainChecker();
    // Gated on the flag: until it is on, a prefixed url still renders for anonymous crawlers and must keep pointing
    // at the unprefixed canonical rather than competing with it.
    if (isMaltDotCom() && isMaltComSeoEnabled.value) {
        // `robots` is then the only indexability gate: a self-canonical on a noindex page is inert.
        linkConfig.push({
            rel: 'canonical' as const,
            href: `${useGuestHost()}${route.path}`,
        });
    } else if (profile.value?.privacy.indexableOnSearchEngines) {
        linkConfig.push({
            rel: 'canonical' as const,
            href: profile.value?.profileCanonicalUrl,
        });
    }
    profile.value?.profileAlternates?.forEach((alternate) => {
        linkConfig.push({
            rel: 'alternate' as const,
            href: alternate.href,
            hreflang: alternate.hrefLang,
        });
    });
    useHead({
        title: `${displayName.value}, ${profile.value?.headline}`,
        titleTemplate: (titleChunk) => titleChunk || '',
        link: linkConfig,
    });

    if (import.meta.server) {
        useHTMLAttrsLang();
        useBreadcrumbMicroData();
        useProfileMicroData();
    }

    const AsyncProfileViewBlocker = defineAsyncComponent(() => import('~/features/profile-signup-prompt/ui/ProfileViewBlocker.vue'));
    const AsyncAdminComponentsWrapper = defineAsyncComponent(() => import('@/components/wrapper/AdminComponentsWrapper.vue'));
    const AsyncEditionComponentsWrapper = defineAsyncComponent(() => import('@/components/wrapper/EditionComponentsWrapper.vue'));
    const AsyncExternalUserComponentsWrapper = defineAsyncComponent(() => import('@/components/wrapper/ExternalUserComponentsWrapper.vue'));

    const layoutWidth = '1088px';

    const shouldStartAmplitudeSessionReplayForProfileNonSelfView = computed(
        () => isAmplitudeRecordingForProfileNonSelfViewEnabled.value && !!loggedIn.value && profile.value?.editMode === false,
    );
    const shouldStartAmplitudeSessionReplayForProfileSelfView = computed(
        () => isAmplitudeRecordingForProfileSelfViewEnabled.value && !!loggedIn.value && profile.value?.editMode === true,
    );
    const shouldStartAmplitudeSessionReplayForProfileUnlogguedView = computed(
        () => isAmplitudeRecordingForProfileUnlogguedViewEnabled.value && !loggedIn.value,
    );
    const shouldStartAmplitudeSessionReplay = computed(
        () =>
            shouldStartAmplitudeSessionReplayForProfileNonSelfView.value ||
            shouldStartAmplitudeSessionReplayForProfileSelfView.value ||
            shouldStartAmplitudeSessionReplayForProfileUnlogguedView.value,
    );
</script>
<style lang="scss">
    body:has(.profile-wrapper--grey) {
        background-color: var(--joy-color-neutral-10);
    }
</style>
<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .profile-wrapper {
        --template-sidebar-width: 360px;
        --template-layout-width: v-bind(layoutWidth);

        &__header {
            margin: 0 0 var(--joy-core-spacing-8);

            @include mq.screen_sm {
                margin: 0;
            }
        }

        &__profile-header-alerts:has(*) {
            margin-bottom: var(--joy-core-spacing-8);
        }

        &__main {
            width: 100%;
        }

        &__main-content {
            flex: 1;
            min-width: 0; // important to make the text ellipsis work
        }

        &__layout {
            margin: 0 auto;
            padding: var(--joy-core-spacing-8);
            display: flex;
            column-gap: var(--joy-core-spacing-8);
            max-width: var(--template-layout-width);

            @include mq.screen_sm {
                padding: var(--joy-core-spacing-4);
            }
        }

        &__side-menu {
            min-width: var(--template-sidebar-width);
            max-width: var(--template-sidebar-width);

            @include mq.screen_xxs(max) {
                min-width: unset;
            }

            @include mq.screen_sm {
                max-width: unset;
            }
        }

        &__content {
            display: flex;
            column-gap: var(--joy-core-spacing-8);

            @include mq.screen_sm {
                display: block;
            }
        }
    }

    :deep(.joy-panel__footer) {
        margin: 0 !important;
    }
</style>
<style lang="scss">
    html {
        scroll-behavior: smooth;
    }

    .profile-section {
        .joy-panel__header {
            align-items: center !important;
        }
    }
</style>
